/**
 * WalkArea — editor-side authoring state + viewport overlay for the walking-path
 * feature. Holds:
 *   - a floor PLANE defined by 3 clicked points (deterministic, tilt-aware)
 *   - the GROUND boundary polygon drawn on that plane (vertices coplanar)
 *   - extra LEVELS: additional walkable floors, each a horizontal plane at its
 *     own height with its own boundary polygon
 *   - STAIRS: quad regions ramping between two heights, connecting floors
 *   - the camera EYE HEIGHT
 *
 * Renders on the gizmo layer (same line-mesh + debug-shader pattern as Markers):
 * the 3 floor picks + the plane triangle, each level's boundary loop (color per
 * level, vertex handles on the ACTIVE level), stair quads with ramp rungs, and
 * a dashed preview segment to the cursor.
 *
 * Exports to scene.json via serialize() → { floor:{origin,normal},
 * boundary:[[x,z]…], eyeHeight, levels:[{height,boundary}…],
 * stairs:[{quad,lowHeight,highHeight}…] }. All non-trivial math (plane build,
 * height, point-in-polygon, clamp, surface resolve) lives in walk-geometry.ts —
 * shared with viewer2.
 */

import {
    Entity,
    Mesh,
    MeshInstance,
    PRIMITIVE_LINES,
    Quat,
    SEMANTIC_COLOR,
    SEMANTIC_POSITION,
    ShaderMaterial,
    Vec3
} from 'playcanvas';
import { seedLineMesh } from './line-mesh';

import { Element, ElementType } from '../element';
import type { Markers } from './markers';
import type { Events } from '../events';
import type { Scene } from '../scene';
import type { Splat } from '../splat';
import { vertexShader, fragmentShader } from '../shaders/debug-shader';
import {
    type Plane, planeFrom3, planeHeightAt, isDegeneratePlane, horizontalPlane,
    orderStairsQuad, polygonArea, CANONICAL_EYE_HEIGHT, type Vec2
} from './walk-geometry';
import type { WalkConfig, WalkVoxels as WalkVoxelsConfig } from '../../../shared/scene-core/scene-schema';
import type { WalkVoxels } from './walk-voxels';

/** Shortest-arc quaternion rotating unit vector `from` to unit vector `to`.
 *  (Same proven helper the old auto-level used — but fed an EXACT plane now.) */
const quatFromTo = (from: Vec3, to: Vec3): Quat => {
    const d = from.dot(to);
    const q = new Quat();
    if (d > 0.999999) {
        q.set(0, 0, 0, 1);
    } else if (d < -0.999999) {
        const axis = Math.abs(from.x) < 0.9 ? new Vec3(1, 0, 0) : new Vec3(0, 1, 0);
        const perp = new Vec3().cross(from, axis).normalize();
        q.setFromAxisAngle(perp, 180);
    } else {
        const c = new Vec3().cross(from, to);
        q.set(c.x, c.y, c.z, 1 + d);
        const l = Math.hypot(q.x, q.y, q.z, q.w) || 1;
        q.set(q.x / l, q.y / l, q.z / l, q.w / l);
    }
    return q;
};

type RGB = [number, number, number];
const PICK_RGB: RGB = [0, 200, 255];     // cyan floor picks
const PLANE_RGB: RGB = [0, 130, 170];    // faint plane triangle
const VERT_RGB: RGB = [180, 255, 210];   // bright vertex handles (active level)
const PREVIEW_RGB: RGB = [0, 200, 255];  // cyan dashed preview
const STAIR_RGB: RGB = [255, 150, 50];   // orange stair quads
const STAIR_RUNG_RGB: RGB = [190, 110, 40];

// Boundary edge color per level (index 0 = ground), cycled when there are more.
const LEVEL_RGBS: RGB[] = [
    [40, 230, 130],   // ground — green
    [80, 170, 255],   // blue
    [255, 120, 210],  // pink
    [255, 210, 70],   // yellow
    [170, 130, 255]   // purple
];
/** Inactive-level dimming so the level being edited reads instantly. */
const dimRGB = (c: RGB): RGB => [(c[0] * 0.45) | 0, (c[1] * 0.45) | 0, (c[2] * 0.45) | 0];
/** Viewport color of a level's boundary (0 = ground). Shared with the panel. */
const levelRGB = (levelIndex: number): RGB => LEVEL_RGBS[levelIndex % LEVEL_RGBS.length];

const CROSS = 0.12;   // half-size of a pick/vertex cross marker (world units)
const DASH = 0.15;    // dash length for the preview segment (world units)
const DEFAULT_EYE_HEIGHT = CANONICAL_EYE_HEIGHT;

/** An additional walkable floor. `height` stays null until picked/typed. */
interface EditorLevel {
    height: number | null;
    boundary: Vec3[];
}

/** A stairs quad; corners [0],[1] sit at lowHeight, [2],[3] at highHeight. */
interface EditorStairs {
    quad: Vec3[];
    lowHeight: number;
    highHeight: number;
}

/** One in-progress stairs click: a point ON a level's plane + which level. */
interface StairsPick {
    level: number;   // 0 = ground, i = extra level i-1 (same as activeLevel)
    point: Vec3;     // on that level's plane, y = the level's floor height
}

const tmpScreen = new Vec3();

class WalkArea extends Element {
    entity!: Entity;
    mesh!: Mesh;
    material!: ShaderMaterial;
    meshInstance!: MeshInstance;
    dirty = true;

    private _picks: Vec3[] = [];
    private _plane: Plane | null = null;
    private _boundary: Vec3[] = [];          // ground-floor boundary
    private _levels: EditorLevel[] = [];     // EXTRA levels (ground lives above)
    private _stairs: EditorStairs[] = [];
    private _stairsDraft: StairsPick[] = []; // in-progress stair picks (0..3)
    private _activeLevel = 0;                // 0 = ground, i = _levels[i - 1]
    private _preview: Vec3 | null = null;
    private _eyeHeight = DEFAULT_EYE_HEIGHT;
    private _visible = true;

    constructor() {
        super(ElementType.debug);
    }

    add() {
        const { scene } = this;
        const device = scene.graphicsDevice;

        this.material = new ShaderMaterial({
            uniqueName: 'walkAreaMaterial',
            attributes: {
                vertex_position: SEMANTIC_POSITION,
                vertex_color: SEMANTIC_COLOR
            },
            vertexWGSL: vertexShader,
            fragmentWGSL: fragmentShader
        });
        this.material.depthWrite = false;
        this.material.depthTest = false;
        this.material.update();

        this.mesh = new Mesh(device);
        this.mesh.primitive[0] = { baseVertex: 0, type: PRIMITIVE_LINES, base: 0, count: 0 };
        seedLineMesh(this.mesh);
        this.meshInstance = new MeshInstance(this.mesh, this.material, null);
        this.meshInstance.cull = false;

        this.entity = new Entity('walkArea');
        this.entity.addComponent('render', {
            meshInstances: [this.meshInstance],
            layers: [scene.gizmoLayer.id]
        });
        scene.app.root.addChild(this.entity);

        registerWalkAreaEvents(this, scene.events);
    }

    destroy() {
        this.entity?.destroy();
    }

    onPreRender() {
        const visible = this._visible && this.scene.camera.renderOverlays;
        if (visible && this.dirty) {
            this.dirty = false;
            this.rebuildMesh();
        }
        // an empty line mesh is a zero-vertex draw on WebGPU: keep it off
        this.entity.enabled = visible && this.mesh.primitive[0].count > 0;
    }

    // ---- getters --------------------------------------------------------
    get plane(): Plane | null { return this._plane; }
    get picks(): readonly Vec3[] { return this._picks; }
    get boundary(): readonly Vec3[] { return this._boundary; }
    get levels(): readonly EditorLevel[] { return this._levels; }
    get stairs(): readonly EditorStairs[] { return this._stairs; }
    get stairsDraft(): readonly StairsPick[] { return this._stairsDraft; }
    get activeLevel(): number { return this._activeLevel; }
    get eyeHeight(): number { return this._eyeHeight; }
    get hasFloor(): boolean { return this._plane !== null; }
    get visible(): boolean { return this._visible; }

    /** The boundary vertex list edits apply to (ground or the active level). */
    activeBoundary(): Vec3[] {
        if (this._activeLevel === 0) return this._boundary;
        return this._levels[this._activeLevel - 1]?.boundary ?? this._boundary;
    }

    /** Floor height of the active level; null while unset (extra levels only). */
    activeHeight(): number | null {
        if (this._activeLevel === 0) return this._plane ? this._plane.origin[1] : null;
        return this._levels[this._activeLevel - 1]?.height ?? null;
    }

    /** The plane boundary clicks land on for the active level (null if unset). */
    activePlane(): Plane | null {
        if (this._activeLevel === 0) return this._plane;
        const h = this._levels[this._activeLevel - 1]?.height;
        return (h === null || h === undefined) ? null : horizontalPlane(h);
    }

    /**
     * Every COMPLETE level (plane + ≥3 boundary points) as a pickable surface,
     * tagged with its level index. Stairs authoring raycasts the cursor
     * against these: a stairs click must land ON a floor, and which floor it
     * lands on decides which two levels the flight connects.
     */
    pickableLevelSurfaces(): { level: number; plane: Plane; poly: Vec2[] }[] {
        const out: { level: number; plane: Plane; poly: Vec2[] }[] = [];
        if (this._plane && this._boundary.length >= 3) {
            out.push({
                level: 0,
                plane: this._plane,
                poly: this._boundary.map(v => [v.x, v.z] as Vec2)
            });
        }
        this._levels.forEach((l, i) => {
            if (l.height !== null && l.boundary.length >= 3) {
                out.push({
                    level: i + 1,
                    plane: horizontalPlane(l.height),
                    poly: l.boundary.map(v => [v.x, v.z] as Vec2)
                });
            }
        });
        return out;
    }

    // ---- floor (3-click plane) -----------------------------------------
    /** Add one floor pick. On the 3rd valid pick, builds + sets the plane. */
    addFloorPick(p: Vec3): 'need-more' | 'done' | 'degenerate' {
        // Re-picking once a floor exists restarts a fresh floor (and boundary).
        if (this._plane) { this._plane = null; this._picks = []; this._boundary = []; }
        this._picks.push(new Vec3(p.x, p.y, p.z));
        if (this._picks.length < 3) {
            this.markDirty(); this.fireChanged();
            return 'need-more';
        }
        const [a, b, c] = this._picks;
        const A: [number, number, number] = [a.x, a.y, a.z];
        const B: [number, number, number] = [b.x, b.y, b.z];
        const C: [number, number, number] = [c.x, c.y, c.z];
        if (isDegeneratePlane(A, B, C)) {
            this._picks = [];
            this.markDirty(); this.fireChanged();
            this.scene.events.fire('walk.floor.degenerate');
            return 'degenerate';
        }
        this._plane = planeFrom3(A, B, C);
        // Level the whole scene to this floor (the point of the 3 clicks): the
        // floor becomes horizontal at Y=0, so everything downstream is the
        // simple flat-floor case. Resets _plane to horizontal + clears picks.
        this.levelSceneToFloor();
        this.markDirty(); this.fireChanged();
        this.scene.events.fire('walk.floor.set', this._plane);
        return 'done';
    }

    resetFloor() {
        this._picks = [];
        this._plane = null;
        this._preview = null;
        this._activeLevel = 0;
        this.markDirty(); this.fireChanged();
    }

    setPlane(plane: Plane) {
        this._plane = plane;
        this.markDirty(); this.fireChanged();
    }

    /**
     * Rotate every splat + model so the current floor plane becomes horizontal
     * (normal → +Y) and the floor sits at world Y = 0. Rotation is about the
     * floor pivot O, then the scene is dropped by O.y. After this the working
     * plane is the trivial horizontal plane, so boundary drawing + the viewer
     * are pure flat-floor cases. Not undoable (matches the prior auto-level);
     * re-picking a level floor is a near-no-op so it's safe to redo.
     */
    private levelSceneToFloor() {
        const plane = this._plane;
        if (!plane) return;
        const N = new Vec3(plane.normal[0], plane.normal[1], plane.normal[2]);
        if (N.length() < 1e-6) return;
        N.normalize();
        const R = quatFromTo(N, new Vec3(0, 1, 0));
        const O = new Vec3(plane.origin[0], plane.origin[1], plane.origin[2]);
        const scene = this.scene;
        const tmp = new Vec3();

        // World transform after leveling: pos' = O + R·(pos − O), then drop O.y.
        const leveled = (ent: any): { pos: Vec3; rot: Quat } => {
            const p = ent.getLocalPosition();
            tmp.set(p.x - O.x, p.y - O.y, p.z - O.z);
            R.transformVector(tmp, tmp);
            return {
                pos: new Vec3(O.x + tmp.x, tmp.y, O.z + tmp.z),
                rot: new Quat().mul2(R, ent.getLocalRotation())
            };
        };

        // Splats go through move() so world bounds + 'splat.moved' fire.
        for (const el of scene.getElementsByType(ElementType.splat)) {
            const s = el as Splat;
            if (!s.entity) continue;
            const { pos, rot } = leveled(s.entity);
            s.move(pos, rot);
        }
        // Models move with the scene so props stay aligned to the floor.
        for (const el of scene.getElementsByType(ElementType.model)) {
            const ent = (el as any).entity;
            if (!ent) continue;
            const { pos, rot } = leveled(ent);
            ent.setLocalPosition(pos);
            ent.setLocalRotation(rot);
        }
        // The collision voxels are another rigid layer of the same capture.
        const voxels = scene.events.invoke('walk.voxels') as WalkVoxels | undefined;
        if (voxels?.entity) {
            const { pos, rot } = leveled(voxels.entity);
            voxels.entity.setLocalPosition(pos);
            voxels.entity.setLocalRotation(rot);
            voxels.changed();
        }
        // Markers and the camera start pose are stored in world space too, so
        // the same rotate-about-O-then-drop-O.y transform that levels the splats
        // must be applied to them, or they'd be left behind floating off (and
        // aiming at) the old, pre-leveled scene.
        const levelPoint = (a: number[]): number[] => {
            tmp.set(a[0] - O.x, a[1] - O.y, a[2] - O.z);
            R.transformVector(tmp, tmp);
            return [O.x + tmp.x, tmp.y, O.z + tmp.z];
        };
        (scene.events.invoke('markers') as Markers | undefined)?.transformPositions((p: Vec3) => {
            const [x, y, z] = levelPoint([p.x, p.y, p.z]);
            p.set(x, y, z);
        });
        scene.events.fire('camera.transformStartPose', levelPoint);

        // Extra levels + stairs were authored in the old orientation too.
        for (const l of this._levels) {
            for (const v of l.boundary) {
                const [x, y, z] = levelPoint([v.x, v.y, v.z]);
                v.set(x, y, z);
            }
            if (l.height !== null && l.boundary.length > 0) {
                // Boundaries stay horizontal per level: re-derive the height
                // from the transformed vertices (average Y) and re-flatten.
                let sum = 0;
                for (const v of l.boundary) sum += v.y;
                l.height = sum / l.boundary.length;
                for (const v of l.boundary) v.y = l.height;
            }
        }
        for (const s of this._stairs) {
            for (const v of s.quad) {
                const [x, y, z] = levelPoint([v.x, v.y, v.z]);
                v.set(x, y, z);
            }
            if (s.quad.length === 4) {
                s.lowHeight = (s.quad[0].y + s.quad[1].y) / 2;
                s.highHeight = (s.quad[2].y + s.quad[3].y) / 2;
                s.quad[0].y = s.quad[1].y = s.lowHeight;
                s.quad[2].y = s.quad[3].y = s.highHeight;
            }
        }

        scene.boundDirty = true;
        scene.forceRender = true;

        // Working floor is now horizontal at Y = 0; picks no longer apply.
        this._plane = { origin: [0, 0, 0], normal: [0, 1, 0] };
        this._picks = [];
    }

    // ---- levels ---------------------------------------------------------
    /** Add an extra level (height unset until picked/typed) and select it. */
    addLevel(): number {
        this._levels.push({ height: null, boundary: [] });
        this._activeLevel = this._levels.length;
        this.markDirty(); this.fireChanged();
        this.scene.events.fire('walk.level.added', this._activeLevel);
        return this._activeLevel;
    }

    /** Select which level boundary edits apply to (0 = ground). */
    selectLevel(i: number) {
        const v = Math.max(0, Math.min(this._levels.length, Math.floor(i)));
        if (v === this._activeLevel) return;
        this._activeLevel = v;
        this._preview = null;
        this.markDirty(); this.fireChanged();
    }

    /** Remove an extra level (i ≥ 1; the ground floor can't be removed). */
    removeLevel(i: number) {
        if (i < 1 || i > this._levels.length) return;
        this._levels.splice(i - 1, 1);
        if (this._activeLevel === i) this._activeLevel = 0;
        else if (this._activeLevel > i) this._activeLevel -= 1;
        // A stairs draft references levels by index — drop it rather than let
        // its picks silently point at the wrong (shifted) floor.
        this._stairsDraft = [];
        this.markDirty(); this.fireChanged();
    }

    /** Set an extra level's floor height (i ≥ 1) and re-seat its boundary. */
    setLevelHeight(i: number, h: number) {
        if (i < 1 || i > this._levels.length || !Number.isFinite(h)) return;
        const level = this._levels[i - 1];
        const hadHeight = level.height !== null;
        if (level.height === h) return;
        const oldHeight = level.height;
        level.height = h;
        for (const v of level.boundary) v.y = h;
        // Stairs ends that sat exactly at this level's floor follow it, so a
        // later height tweak doesn't detach the flights connected to it.
        if (oldHeight !== null) {
            for (let s = 0; s < this._stairs.length; s++) {
                const st = this._stairs[s];
                const low = Math.abs(st.lowHeight - oldHeight) < 1e-6 ? h : st.lowHeight;
                const high = Math.abs(st.highHeight - oldHeight) < 1e-6 ? h : st.highHeight;
                if (low !== st.lowHeight || high !== st.highHeight) this.setStairsHeights(s, low, high);
            }
        }
        this.markDirty(); this.fireChanged();
        if (!hadHeight) this.scene.events.fire('walk.level.heightSet', i);
    }

    // ---- stairs ---------------------------------------------------------
    beginStairs() {
        if (this._stairsDraft.length === 0) return;
        this._stairsDraft = [];
        this.markDirty(); this.fireChanged();
    }

    cancelStairs() {
        if (this._stairsDraft.length === 0) return;
        this._stairsDraft = [];
        this.markDirty(); this.fireChanged();
    }

    undoStairsPick() {
        if (this._stairsDraft.length === 0) return;
        this._stairsDraft.pop();
        this.markDirty(); this.fireChanged();
    }

    /**
     * Add one stairs pick — a point ON a level's floor plane (raycast by the
     * tool and snapped to that level's boundary edge, so flights run from the
     * SIDE of one walking area to the side of the other), tagged with that
     * level. The flight is drawn as two SIDE lines:
     *
     *   pick 1: a point on one floor          ┐ side 1 — the sloped line
     *   pick 2: the opposite point on ANOTHER ┘ between them is one limit
     *           floor (this decides which two    of the stairs
     *           levels the flight connects)
     *   pick 3: the other side's point on either of those two floors ┐ side 2
     *   pick 4: its opposite point on the remaining floor            ┘
     *
     * The stairs quad spans the two side lines; end heights come EXACTLY from
     * the two levels (no snapping), so the flight touches both floors by
     * construction. Picks on the wrong floor are rejected with guidance.
     */
    addStairsPick(level: number, p: Vec3): 'need-more' | 'done' | 'rejected' {
        const d = this._stairsDraft;
        const reject = (msg: string): 'rejected' => {
            this.scene.events.fire('walk.status', msg);
            return 'rejected';
        };
        if (d.length === 1) {
            if (level === d[0].level) {
                return reject('Same floor — click the opposite point on the OTHER floor to finish this side.');
            }
        } else if (d.length === 2) {
            if (level !== d[0].level && level !== d[1].level) {
                return reject('Click on one of the two floors this flight connects.');
            }
        } else if (d.length === 3) {
            const partner = d[2].level === d[0].level ? d[1].level : d[0].level;
            if (level !== partner) {
                return reject('Click the opposite point on the OTHER floor to finish the second side.');
            }
        }
        d.push({ level, point: new Vec3(p.x, p.y, p.z) });
        if (d.length < 4) {
            this.markDirty(); this.fireChanged();
            return 'need-more';
        }

        // Two sides: (d0 → d1) and (d2 → d3), each bridging the SAME pair of
        // levels. Group corners by which level of the pair they sit on.
        const heightOf = (pk: StairsPick) => pk.point.y;
        const lowLevel = heightOf(d[0]) <= heightOf(d[1]) ? d[0].level : d[1].level;
        const lowOf = (a: StairsPick, b: StairsPick) => (a.level === lowLevel ? a : b);
        const highOf = (a: StairsPick, b: StairsPick) => (a.level === lowLevel ? b : a);
        const s1l = lowOf(d[0], d[1]), s1h = highOf(d[0], d[1]);
        const s2l = lowOf(d[2], d[3]), s2h = highOf(d[2], d[3]);
        const low = (s1l.point.y + s2l.point.y) / 2;
        const high = (s1h.point.y + s2h.point.y) / 2;

        // Quad [low1, low2, high2, high1]; orderStairsQuad de-bowties it if
        // the two drawn sides cross each other.
        const q = orderStairsQuad(
            [s1l.point.x, s1l.point.z], [s2l.point.x, s2l.point.z],
            [s2h.point.x, s2h.point.z], [s1h.point.x, s1h.point.z]
        );
        this._stairsDraft = [];
        if (Math.abs(polygonArea(q)) < 0.01) {
            this.markDirty(); this.fireChanged();
            return reject('Stairs footprint too thin — draw the two sides further apart.');
        }
        this._stairs.push({
            quad: [
                new Vec3(q[0][0], low, q[0][1]),
                new Vec3(q[1][0], low, q[1][1]),
                new Vec3(q[2][0], high, q[2][1]),
                new Vec3(q[3][0], high, q[3][1])
            ],
            lowHeight: low,
            highHeight: high
        });
        this.markDirty(); this.fireChanged();
        this.scene.events.fire('walk.stairs.complete', this._stairs.length - 1);
        return 'done';
    }

    removeStairs(i: number) {
        if (i < 0 || i >= this._stairs.length) return;
        this._stairs.splice(i, 1);
        this.markDirty(); this.fireChanged();
    }

    setStairsHeights(i: number, low: number, high: number) {
        if (i < 0 || i >= this._stairs.length) return;
        if (!Number.isFinite(low) || !Number.isFinite(high)) return;
        const s = this._stairs[i];
        // The stored quad keeps the low edge first; re-orient if the edit
        // flipped which end is lower.
        if (low > high) {
            s.quad = [s.quad[2], s.quad[3], s.quad[0], s.quad[1]];
            const t = low; low = high; high = t;
        }
        s.lowHeight = low;
        s.highHeight = high;
        s.quad[0].y = s.quad[1].y = low;
        s.quad[2].y = s.quad[3].y = high;
        this.markDirty(); this.fireChanged();
    }

    // ---- boundary (active level) ----------------------------------------
    addVertex(p: Vec3) {
        this.activeBoundary().push(new Vec3(p.x, p.y, p.z));
        this.markDirty(); this.fireChanged();
    }

    moveVertex(i: number, p: Vec3) {
        const b = this.activeBoundary();
        if (i < 0 || i >= b.length) return;
        b[i].set(p.x, p.y, p.z);
        this.markDirty(); this.fireChanged();
    }

    /** Insert a vertex on the edge that starts at vertex `after`. */
    insertVertex(after: number, p: Vec3) {
        const b = this.activeBoundary();
        if (after < 0 || after >= b.length) return;
        b.splice(after + 1, 0, new Vec3(p.x, p.y, p.z));
        this.markDirty(); this.fireChanged();
    }

    /** Move one stairs corner in XZ; its height stays the flight's end height. */
    moveStairsCorner(i: number, corner: number, x: number, z: number) {
        const s = this._stairs[i];
        if (!s || corner < 0 || corner > 3) return;
        s.quad[corner].x = x;
        s.quad[corner].z = z;
        this.markDirty(); this.fireChanged();
    }

    removeVertex(i: number) {
        const b = this.activeBoundary();
        if (i < 0 || i >= b.length) return;
        b.splice(i, 1);
        this.markDirty(); this.fireChanged();
    }

    removeLastVertex() {
        const b = this.activeBoundary();
        if (b.length === 0) return;
        b.pop();
        this.markDirty(); this.fireChanged();
    }

    /** Screen-pick the nearest ACTIVE-level boundary vertex; -1 if none close. */
    nearestVertex(screenX: number, screenY: number, pixelRadius = 14): number {
        const cam = this.scene.camera;
        const canvas = this.scene.canvas;
        const rect = canvas.getBoundingClientRect();
        const boundary = this.activeBoundary();
        let best = -1;
        let bestD2 = pixelRadius * pixelRadius;
        const tmp = new Vec3();
        for (let i = 0; i < boundary.length; i++) {
            const v = boundary[i];
            cam.worldToScreen(v, tmp);
            // worldToScreen returns NORMALIZED coords in this build (see
            // markers-tool.ts pickMarker) — scale to canvas pixels.
            const px = tmp.x * rect.width;
            const py = tmp.y * rect.height;
            const dx = px - screenX;
            const dy = py - screenY;
            const d2 = dx * dx + dy * dy;
            if (d2 < bestD2) { bestD2 = d2; best = i; }
        }
        return best;
    }

    /** Canvas-pixel position of a world point, or null when it is behind the camera. */
    private toScreen(v: Vec3, out: { x: number; y: number }): boolean {
        const cam = this.scene.camera;
        const rect = this.scene.canvas.getBoundingClientRect();
        const fwd = cam.mainCamera.forward;
        const eye = cam.mainCamera.getPosition();
        if ((v.x - eye.x) * fwd.x + (v.y - eye.y) * fwd.y + (v.z - eye.z) * fwd.z <= 0) return false;
        cam.worldToScreen(v, tmpScreen);
        out.x = tmpScreen.x * rect.width;
        out.y = tmpScreen.y * rect.height;
        return true;
    }

    /**
     * Screen-pick the nearest ACTIVE-level boundary edge (closing edge
     * included): the index of its first vertex, -1 if none within the radius.
     */
    nearestEdge(screenX: number, screenY: number, pixelRadius = 10): number {
        const b = this.activeBoundary();
        if (b.length < 2) return -1;
        const a = { x: 0, y: 0 }, c = { x: 0, y: 0 };
        let best = -1;
        let bestD = pixelRadius;
        for (let i = 0; i < b.length; i++) {
            if (!this.toScreen(b[i], a) || !this.toScreen(b[(i + 1) % b.length], c)) continue;
            const ex = c.x - a.x, ey = c.y - a.y;
            const l2 = ex * ex + ey * ey || 1;
            const u = Math.max(0, Math.min(1, ((screenX - a.x) * ex + (screenY - a.y) * ey) / l2));
            const d = Math.hypot(a.x + ex * u - screenX, a.y + ey * u - screenY);
            if (d < bestD) { bestD = d; best = i; }
        }
        return best;
    }

    /** Screen-pick the nearest stairs corner: { stairs, corner }, or null. */
    nearestStairsCorner(screenX: number, screenY: number, pixelRadius = 12): { stairs: number; corner: number } | null {
        const q = { x: 0, y: 0 };
        let best: { stairs: number; corner: number } | null = null;
        let bestD = pixelRadius;
        this._stairs.forEach((s, i) => s.quad.forEach((v, k) => {
            if (!this.toScreen(v, q)) return;
            const d = Math.hypot(q.x - screenX, q.y - screenY);
            if (d < bestD) { bestD = d; best = { stairs: i, corner: k }; }
        }));
        return best;
    }

    /** World position of a stairs corner (for dragging it on its own height). */
    stairsCorner(i: number, corner: number): Vec3 | null {
        return this._stairs[i]?.quad[corner] ?? null;
    }

    setPreview(p: Vec3 | null) {
        this._preview = p ? new Vec3(p.x, p.y, p.z) : null;
        this.markDirty(); // frequent; no fireChanged (no model change)
    }

    setEyeHeight(h: number) {
        const v = Number.isFinite(h) ? Math.max(0.1, h) : DEFAULT_EYE_HEIGHT;
        if (v === this._eyeHeight) return;
        this._eyeHeight = v;
        this.fireChanged();
    }

    setVisible(v: boolean) {
        if (v === this._visible) return;
        this._visible = v;
        this.scene.forceRender = true;
        this.scene.events.fire('walk.visibleChanged', v);
    }

    /** Drop the ACTIVE level's boundary loop (keeps the floor/level itself). */
    clearBoundary() {
        const b = this.activeBoundary();
        if (b.length === 0) return;
        b.length = 0;
        this._preview = null;
        this.markDirty(); this.fireChanged();
    }

    clear() {
        const had = this._picks.length || this._plane || this._boundary.length ||
            this._levels.length || this._stairs.length || this._stairsDraft.length;
        this._picks = [];
        this._plane = null;
        this._boundary = [];
        this._levels = [];
        this._stairs = [];
        this._stairsDraft = [];
        this._activeLevel = 0;
        this._preview = null;
        if (had) { this.markDirty(); this.fireChanged(); }
    }

    // ---- (de)serialize --------------------------------------------------
    /** scene.json `walk` block. null when not export-worthy (no plane or <3 verts). */
    serialize(): WalkConfig | null {
        if (!this._plane || this._boundary.length < 3) return null;
        const r = (n: number) => Math.round(n * 1e4) / 1e4;
        const o = this._plane.origin;
        const n = this._plane.normal;
        const out: WalkConfig = {
            floor: {
                origin: [r(o[0]), r(o[1]), r(o[2])],
                normal: [r(n[0]), r(n[1]), r(n[2])]
            },
            boundary: this._boundary.map((v): [number, number] => [r(v.x), r(v.z)]),
            eyeHeight: r(this._eyeHeight)
        };
        // Only complete extra levels/stairs export; partial work stays in the
        // .ssproj snapshot but never reaches the viewer.
        const levels = this._levels
            .filter(l => l.height !== null && Number.isFinite(l.height) && l.boundary.length >= 3)
            .map(l => ({
                height: r(l.height!),
                boundary: l.boundary.map((v): [number, number] => [r(v.x), r(v.z)])
            }));
        if (levels.length > 0) out.levels = levels;
        const stairs = this._stairs
            .filter(s => s.quad.length === 4)
            .map(s => ({
                quad: s.quad.map((v): [number, number] => [r(v.x), r(v.z)]),
                lowHeight: r(s.lowHeight),
                highHeight: r(s.highHeight)
            }));
        if (stairs.length > 0) out.stairs = stairs;
        const voxels = this.scene.events.invoke('walk.voxels.serialize');
        if (voxels) out.voxels = voxels;
        return out;
    }

    /**
     * Full editor-state snapshot for .ssproj round-trip — unlike serialize()
     * this persists partial work (floor set but boundary unfinished, a level
     * with no height yet) so a reload doesn't lose it.
     */
    snapshot(): {
        floor: { origin: number[]; normal: number[] } | null;
        boundary: number[][];
        eyeHeight: number;
        levels: { height: number | null; boundary: number[][] }[];
        stairs: { quad: number[][]; lowHeight: number; highHeight: number }[];
        voxels?: WalkVoxelsConfig;
    } {
        const voxels = this.scene.events.invoke('walk.voxels.serialize') as WalkVoxelsConfig | null;
        return {
            ...(voxels ? { voxels } : {}),
            floor: this._plane
                ? { origin: [...this._plane.origin], normal: [...this._plane.normal] }
                : null,
            boundary: this._boundary.map(v => [v.x, v.z]),
            eyeHeight: this._eyeHeight,
            levels: this._levels.map(l => ({
                height: l.height,
                boundary: l.boundary.map(v => [v.x, v.z])
            })),
            stairs: this._stairs.map(s => ({
                quad: s.quad.map(v => [v.x, v.z]),
                lowHeight: s.lowHeight,
                highHeight: s.highHeight
            }))
        };
    }

    deserialize(data: any) {
        this._picks = [];
        this._boundary = [];
        this._levels = [];
        this._stairs = [];
        this._stairsDraft = [];
        this._activeLevel = 0;
        this._preview = null;
        this._plane = null;
        if (data && typeof data === 'object') {
            const f = data.floor;
            if (f && Array.isArray(f.origin) && Array.isArray(f.normal) &&
                f.origin.length >= 3 && f.normal.length >= 3) {
                this._plane = {
                    origin: [f.origin[0], f.origin[1], f.origin[2]],
                    normal: [f.normal[0], f.normal[1], f.normal[2]]
                };
            }
            if (this._plane && Array.isArray(data.boundary)) {
                for (const pt of data.boundary) {
                    if (Array.isArray(pt) && pt.length >= 2) {
                        const x = pt[0], z = pt[1];
                        this._boundary.push(new Vec3(x, planeHeightAt(this._plane, x, z), z));
                    }
                }
            }
            if (Array.isArray(data.levels)) {
                for (const l of data.levels) {
                    if (!l || typeof l !== 'object' || !Array.isArray(l.boundary)) continue;
                    const height = (typeof l.height === 'number' && Number.isFinite(l.height)) ? l.height : null;
                    const boundary: Vec3[] = [];
                    for (const pt of l.boundary) {
                        if (Array.isArray(pt) && pt.length >= 2) {
                            boundary.push(new Vec3(pt[0], height ?? 0, pt[1]));
                        }
                    }
                    this._levels.push({ height, boundary });
                }
            }
            if (Array.isArray(data.stairs)) {
                for (const s of data.stairs) {
                    if (!s || typeof s !== 'object' || !Array.isArray(s.quad)) continue;
                    if (s.quad.length !== 4) continue;
                    if (!Number.isFinite(s.lowHeight) || !Number.isFinite(s.highHeight)) continue;
                    const quad = s.quad.map((pt: number[], i: number) => new Vec3(
                        pt[0], i < 2 ? s.lowHeight : s.highHeight, pt[1]
                    ));
                    if (quad.some((v: Vec3) => !Number.isFinite(v.x) || !Number.isFinite(v.z))) continue;
                    this._stairs.push({ quad, lowHeight: s.lowHeight, highHeight: s.highHeight });
                }
            }
            if (typeof data.eyeHeight === 'number' && Number.isFinite(data.eyeHeight)) {
                this._eyeHeight = Math.max(0.1, data.eyeHeight);
            }
            if (data.voxels) this.scene.events.fire('walk.voxels.deserialize', data.voxels);
        }
        this.markDirty(); this.fireChanged();
    }

    // ---- internals ------------------------------------------------------
    private fireChanged() { this.scene.events.fire('walk.changed'); }
    private markDirty() { this.dirty = true; this.scene.forceRender = true; }

    private rebuildMesh() {
        const pos: number[] = [];
        const col: number[] = [];

        const pushLine = (ax: number, ay: number, az: number, bx: number, by: number, bz: number, rgb: RGB) => {
            pos.push(ax, ay, az, bx, by, bz);
            for (let k = 0; k < 2; k++) col.push(rgb[0], rgb[1], rgb[2], 255);
        };
        const pushCross = (p: Vec3, half: number, rgb: RGB) => {
            pushLine(p.x - half, p.y, p.z, p.x + half, p.y, p.z, rgb);
            pushLine(p.x, p.y, p.z - half, p.x, p.y, p.z + half, rgb);
            pushLine(p.x, p.y - half, p.z, p.x, p.y + half, p.z, rgb);
        };
        const pushDashed = (a: Vec3, b: Vec3, rgb: RGB) => {
            const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
            const len = Math.hypot(dx, dy, dz);
            if (len < 1e-5) return;
            // Cap segment count so a long preview can't explode the line buffer.
            const steps = Math.min(80, Math.max(1, Math.floor(len / DASH)));
            for (let i = 0; i < steps; i += 2) {
                const t0 = i / steps, t1 = Math.min(1, (i + 1) / steps);
                pushLine(a.x + dx * t0, a.y + dy * t0, a.z + dz * t0,
                         a.x + dx * t1, a.y + dy * t1, a.z + dz * t1, rgb);
            }
        };
        const pushBoundary = (b: readonly Vec3[], rgb: RGB, active: boolean) => {
            const edge = active ? rgb : dimRGB(rgb);
            const n = b.length;
            if (active) for (let i = 0; i < n; i++) pushCross(b[i], CROSS, VERT_RGB);
            for (let i = 0; i + 1 < n; i++) pushLine(b[i].x, b[i].y, b[i].z, b[i + 1].x, b[i + 1].y, b[i + 1].z, edge);
            if (n >= 3) pushLine(b[n - 1].x, b[n - 1].y, b[n - 1].z, b[0].x, b[0].y, b[0].z, edge);
        };

        // Floor picks + plane triangle.
        for (const p of this._picks) pushCross(p, CROSS, PICK_RGB);
        if (this._picks.length === 3) {
            const [a, b, c] = this._picks;
            pushLine(a.x, a.y, a.z, b.x, b.y, b.z, PLANE_RGB);
            pushLine(b.x, b.y, b.z, c.x, c.y, c.z, PLANE_RGB);
            pushLine(c.x, c.y, c.z, a.x, a.y, a.z, PLANE_RGB);
        }

        // Boundaries: ground + each extra level, active one with vertex handles.
        pushBoundary(this._boundary, levelRGB(0), this._activeLevel === 0);
        for (let i = 0; i < this._levels.length; i++) {
            pushBoundary(this._levels[i].boundary, levelRGB(i + 1), this._activeLevel === i + 1);
        }

        // Stairs quads: outline at their real corner heights + ramp rungs.
        for (const s of this._stairs) {
            const q = s.quad;
            if (q.length !== 4) continue;
            for (let i = 0; i < 4; i++) {
                const a = q[i], b = q[(i + 1) % 4];
                pushLine(a.x, a.y, a.z, b.x, b.y, b.z, STAIR_RGB);
            }
            // Rungs between the side edges (q0→q3 and q1→q2) suggest the ramp.
            for (const t of [0.25, 0.5, 0.75]) {
                const ax = q[0].x + (q[3].x - q[0].x) * t;
                const ay = q[0].y + (q[3].y - q[0].y) * t;
                const az = q[0].z + (q[3].z - q[0].z) * t;
                const bx = q[1].x + (q[2].x - q[1].x) * t;
                const by = q[1].y + (q[2].y - q[1].y) * t;
                const bz = q[1].z + (q[2].z - q[1].z) * t;
                pushLine(ax, ay, az, bx, by, bz, STAIR_RUNG_RGB);
            }
        }

        // In-progress stair picks: corner crosses; once a side has both ends
        // it becomes a SOLID sloped line (a limit of the flight); the point
        // being placed previews as a dashed line from the last pick.
        const draft = this._stairsDraft;
        for (const pk of draft) pushCross(pk.point, CROSS, STAIR_RGB);
        if (draft.length >= 2) pushLine(
            draft[0].point.x, draft[0].point.y, draft[0].point.z,
            draft[1].point.x, draft[1].point.y, draft[1].point.z, STAIR_RGB
        );
        if (draft.length > 0 && this._preview) {
            pushDashed(draft[draft.length - 1].point, this._preview, STAIR_RGB);
        }

        // Dashed preview from the active boundary's last vertex to the cursor
        // (boundary drawing only — stairs drafting owns the preview above).
        const ab = this.activeBoundary();
        if (this._preview && draft.length === 0 && ab.length > 0) {
            pushDashed(ab[ab.length - 1], this._preview, PREVIEW_RGB);
        }

        if (pos.length === 0) {
            this.mesh.primitive[0].count = 0;
            return;
        }
        this.mesh.setPositions(pos);
        this.mesh.setColors32(new Uint8Array(col));
        this.mesh.update(PRIMITIVE_LINES);
    }
}

// ----------------------------------------------------------------------------
// Event wiring — `walk.*` namespace.
// ----------------------------------------------------------------------------

const registerWalkAreaEvents = (walk: WalkArea, events: Events) => {
    events.function('walk', () => walk);
    events.function('walk.serialize', () => walk.serialize());
    events.function('walk.snapshot', () => walk.snapshot());

    events.on('walk.floor.pick', (p: Vec3) => walk.addFloorPick(p));
    events.on('walk.floor.reset', () => walk.resetFloor());
    events.on('walk.vertex.add', (p: Vec3) => walk.addVertex(p));
    events.on('walk.vertex.move', (i: number, p: Vec3) => walk.moveVertex(i, p));
    events.on('walk.vertex.remove', (i: number) => walk.removeVertex(i));
    events.on('walk.vertex.insert', (after: number, p: Vec3) => walk.insertVertex(after, p));
    events.on('walk.stairs.corner', (i: number, corner: number, x: number, z: number) => walk.moveStairsCorner(i, corner, x, z));
    events.on('walk.vertex.removeLast', () => walk.removeLastVertex());
    events.on('walk.boundary.clear', () => walk.clearBoundary());
    events.on('walk.level.add', () => walk.addLevel());
    events.on('walk.level.select', (i: number) => walk.selectLevel(i));
    events.on('walk.level.remove', (i: number) => walk.removeLevel(i));
    events.on('walk.level.height', (i: number, h: number) => walk.setLevelHeight(i, h));
    events.on('walk.stairs.begin', () => walk.beginStairs());
    events.on('walk.stairs.pick', (level: number, p: Vec3) => walk.addStairsPick(level, p));
    events.on('walk.stairs.cancel', () => walk.cancelStairs());
    events.on('walk.stairs.undoPick', () => walk.undoStairsPick());
    events.on('walk.stairs.remove', (i: number) => walk.removeStairs(i));
    events.on('walk.stairs.heights', (i: number, low: number, high: number) => walk.setStairsHeights(i, low, high));
    events.on('walk.preview', (p: Vec3 | null) => walk.setPreview(p));
    events.on('walk.eyeHeight', (h: number) => walk.setEyeHeight(h));
    events.on('walk.setVisible', (v: boolean) => walk.setVisible(v));
    events.on('walk.clear', () => walk.clear());
    events.on('walk.deserialize', (data: any) => walk.deserialize(data));

    events.on('scene.clear', () => walk.clear());
};

export { WalkArea, levelRGB, STAIR_RGB };
